import { useEffect, useMemo, useState } from 'react';
import type { Form } from '../types/schema';
import type { SingleUnit } from '../types/schema';
import type { tGroupedEquipment, tSelectLabelValue } from '../types';
import { Inspect } from './Inspect';
import { DailyServiceLog } from './DailyServiceLog';
import { filterEquipmentForHomeFavoriteForm } from '../utils/homeFormFavorites';
import { FORM_TYPES, LOCAL_STORAGE_KEYS } from '../utils/constants';
import { resolveFormType } from '../utils/reportUtils';
import { isUnitArchived } from './pages-equipment/equipmentFieldUtils';
import { Btn, Select, Stack } from './ui';
import { CustomModal } from './CustomModal';

type HomeFavoriteFormInspectionModalProps = {
    visible: boolean;
    form: Form | null;
    scopedEquipment: tGroupedEquipment[];
    onDismiss: () => void;
};

type WizardStep = 'pickEquipment' | 'pickUnit' | 'form';

const unitSelectLabel = (unit: SingleUnit): string =>
    unit.description ? `${unit.name} - ${unit.description}` : unit.name || '';

const getEquipmentUnits = (equipment: tGroupedEquipment): SingleUnit[] =>
    equipment.units?.filter((unit): unit is SingleUnit => Boolean(unit?.id) && !isUnitArchived(unit)) ?? [];

const markInspectionStarted = () => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED, new Date().toISOString());
};

const goToFormStep = (
    unit: SingleUnit,
    setSelectedUnitId: (value: tSelectLabelValue) => void,
    setSelectedUnit: (value: SingleUnit) => void,
    setStep: (step: WizardStep) => void,
) => {
    setSelectedUnitId({ label: unitSelectLabel(unit), value: unit.id });
    setSelectedUnit(unit);
    markInspectionStarted();
    setStep('form');
};

export const HomeFavoriteFormInspectionModal = ({
    visible,
    form,
    scopedEquipment,
    onDismiss,
}: HomeFavoriteFormInspectionModalProps) => {
    const [step, setStep] = useState<WizardStep>('pickEquipment');
    const [selectedEquipment, setSelectedEquipment] = useState<tGroupedEquipment | null>(null);
    const [selectedUnitId, setSelectedUnitId] = useState<tSelectLabelValue | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<SingleUnit | null>(null);

    const matchingEquipment = useMemo(() => {
        if (!form) {
            return [];
        }

        return filterEquipmentForHomeFavoriteForm(scopedEquipment, form);
    }, [form, scopedEquipment]);

    const formType = form ? resolveFormType(form) : null;
    const isDailyService = formType === FORM_TYPES.DAILY_SERVICE;

    const resetWizard = () => {
        setStep('pickEquipment');
        setSelectedEquipment(null);
        setSelectedUnitId(null);
        setSelectedUnit(null);
    };

    useEffect(() => {
        if (!visible) {
            resetWizard();
            return;
        }

        if (!form) {
            return;
        }

        const matches = filterEquipmentForHomeFavoriteForm(scopedEquipment, form);
        setStep('pickEquipment');
        setSelectedEquipment(null);
        setSelectedUnitId(null);
        setSelectedUnit(null);

        if (matches.length === 1) {
            const equipment = matches[0];
            setSelectedEquipment(equipment);

            const units = getEquipmentUnits(equipment);
            if (units.length <= 1) {
                const unit = units[0] ?? null;
                if (unit) {
                    setSelectedUnitId({ label: unitSelectLabel(unit), value: unit.id });
                    setSelectedUnit(unit);
                    markInspectionStarted();
                    setStep('form');
                }
                return;
            }

            setStep('pickUnit');
        }
    }, [visible, form?.id, scopedEquipment]);

    useEffect(() => {
        if (!visible || step !== 'pickUnit' || !selectedEquipment) {
            return;
        }

        const units = getEquipmentUnits(selectedEquipment);
        if (units.length === 1 && !selectedUnitId) {
            goToFormStep(units[0], setSelectedUnitId, setSelectedUnit, setStep);
        }
    }, [visible, step, selectedEquipment, selectedUnitId]);

    const handleDismiss = () => {
        resetWizard();
        onDismiss();
    };

    const handleEquipmentContinue = () => {
        if (!selectedEquipment) {
            return;
        }

        const units = getEquipmentUnits(selectedEquipment);
        if (units.length === 0) {
            return;
        }

        if (units.length > 1) {
            setSelectedUnitId(null);
            setSelectedUnit(null);
            setStep('pickUnit');
            return;
        }

        const unit = units[0];
        goToFormStep(unit, setSelectedUnitId, setSelectedUnit, setStep);
    };

    const handleBackFromForm = () => {
        const units = selectedEquipment ? getEquipmentUnits(selectedEquipment) : [];
        if (units.length > 1) {
            setStep('pickUnit');
            return;
        }

        if (matchingEquipment.length > 1) {
            setStep('pickEquipment');
            return;
        }

        handleDismiss();
    };

    const selectedEquipmentUnits = selectedEquipment ? getEquipmentUnits(selectedEquipment) : [];

    const header = form?.name
        ? isDailyService
            ? `${form.name} — Daily service`
            : `${form.name} — Inspection`
        : 'Inspection';

    const isFormStep = step === 'form' && Boolean(selectedEquipment && selectedUnit && selectedUnitId);

    return (
        <CustomModal
            visible={visible}
            onDismiss={handleDismiss}
            size={isFormStep ? 'xlarge' : 'default'}
            bodyClassName={isFormStep ? 'home-flow-modal-body' : undefined}
            header={header}
            footer={
                !form || isFormStep ? undefined : matchingEquipment.length === 0 ? (
                    <div className="modal-footer-actions">
                        <Btn variant="primary" buttonText="Close" buttonOnClick={handleDismiss} />
                    </div>
                ) : step === 'pickEquipment' ? (
                    <div className="modal-footer-actions">
                        <Btn
                            variant="primary"
                            buttonText="Continue"
                            buttonDisabled={!selectedEquipment || selectedEquipmentUnits.length === 0}
                            buttonOnClick={handleEquipmentContinue}
                        />
                    </div>
                ) : step === 'pickUnit' ? (
                    <div className="modal-footer-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Back"
                            buttonOnClick={() => setStep('pickEquipment')}
                        />
                        <Btn
                            variant="primary"
                            buttonText="Start"
                            buttonDisabled={!selectedUnitId}
                            buttonOnClick={() => {
                                markInspectionStarted();
                                setStep('form');
                            }}
                        />
                    </div>
                ) : undefined
            }
        >
            {!form ? null : matchingEquipment.length === 0 ? (
                <p>No equipment is assigned to this form. Assign it on the Equipment page, then try again.</p>
            ) : step === 'pickEquipment' ? (
                <Stack size="m">
                    <h3 className="home-modal-step-title">Select equipment</h3>
                    <Select
                        ariaLabel="Equipment category"
                        value={selectedEquipment?.id ?? ''}
                        options={matchingEquipment.map((equipment) => ({
                            label: equipment.name,
                            value: equipment.id,
                        }))}
                        onChange={(value) => {
                            const equipment = matchingEquipment.find((item) => item.id === value);
                            setSelectedEquipment(equipment ?? null);
                        }}
                        placeholder="Choose equipment category"
                    />
                    {selectedEquipment && selectedEquipmentUnits.length === 0 && (
                        <p>This equipment category has no units yet. Add a unit on the Equipment page, then try again.</p>
                    )}
                </Stack>
            ) : step === 'pickUnit' && selectedEquipment ? (
                <Stack size="m">
                    <h3 className="home-modal-step-title">Select unit</h3>
                    <Select
                        ariaLabel="Unit"
                        value={selectedUnitId?.value ?? ''}
                        options={getEquipmentUnits(selectedEquipment).map((unit) => ({
                                label: unitSelectLabel(unit),
                                value: unit.id,
                            }))}
                        onChange={(value) => {
                            const unit = selectedEquipment.units?.find((item) => item?.id === value) ?? null;
                            setSelectedUnitId(
                                unit ? { label: unitSelectLabel(unit), value: unit.id } : null,
                            );
                            setSelectedUnit(unit);
                        }}
                        placeholder="Choose a unit"
                    />
                </Stack>
            ) : isFormStep && selectedEquipment && selectedUnit && selectedUnitId ? (
                isDailyService ? (
                    <DailyServiceLog
                        equipmentData={selectedEquipment}
                        equipmentId={selectedEquipment.id}
                        selectedUnitId={selectedUnitId}
                        selectedUnit={selectedUnit}
                        backBtnAction={handleBackFromForm}
                    />
                ) : (
                    <Inspect
                        equipmentData={selectedEquipment}
                        equipmentId={selectedEquipment.id}
                        selectedUnitId={selectedUnitId}
                        selectedUnit={selectedUnit}
                        backBtnAction={handleBackFromForm}
                    />
                )
            ) : null}
        </CustomModal>
    );
};
